import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import SpinningWheel from '../components/SpinningWheel';
import PlayerList from '../components/PlayerList';
import JoinModal from '../components/JoinModal';
import WheelConfig from '../components/WheelConfig';
import GameSettings from '../components/GameSettings';
import QRCodeGenerator from '../components/QRCodeGenerator';
import { ClipLoader } from 'react-spinners';
import useGameRoom from '../hooks/useGameRoom';
import useRoomAnalytics from '../hooks/useRoomAnalytics';
import ServerPriorityIndicator from '../components/ServerPriorityIndicator';
import PasswordModal from '../components/PasswordModal';
import LanguageDropdown from '../components/LanguageDropdown';
import supabase from '../utils/supabaseClient';

const Container = styled.div`
  min-height: 100vh;
  background: var(--color-light-bg);
  color: var(--color-dark);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const Header = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: var(--color-secondary);
  border-radius: 18px;
  color: var(--color-light-bg);
  box-shadow: 0 4px 20px rgba(77, 186, 191, 0.2);
`;

const RoomInfo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--color-light-bg);
  border-radius: 18px;
  box-shadow: 0 4px 20px rgba(17, 63, 72, 0.1);
  color: var(--color-dark);
`;

const ErrorMessage = styled.div`
  color: var(--color-primary);
  background: var(--color-light-bg);
  padding: 1rem;
  border-radius: 12px;
  margin: 1rem 0;
  border: 2px solid var(--color-primary);
  text-align: center;
  font-weight: 600;
`;

const Button = styled(motion.button)`
  padding: 15px 30px;
  font-size: 1.1rem;
  font-weight: 700;
  border: ${props => props.isSecondary ? '2px solid var(--color-secondary)' : 'none'};
  border-radius: 12px;
  cursor: pointer;
  background: ${props => props.isSecondary ? 'var(--color-light-bg)' : 'var(--color-primary)'};
  color: ${props => props.isSecondary ? 'var(--color-dark)' : 'var(--color-light-bg)'};
  margin: 0.5rem;
  min-width: 200px;
  transition: all 0.3s ease;
  font-family: 'Fredoka', Arial, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(247, 5, 108, 0.3);
    background: ${props => props.isSecondary ? 'var(--color-secondary)' : 'var(--color-accent)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const PlayersList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
`;

const PlayerCard = styled(motion.div)`
  padding: 1.5rem;
  background: var(--color-secondary);
  border-radius: 18px;
  text-align: center;
  border-left: 6px solid var(--color-accent);
  box-shadow: 0 4px 20px rgba(77, 186, 191, 0.2);
  color: var(--color-light-bg);
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: var(--color-light-bg);
    font-family: 'Fredoka', Arial, sans-serif;
    font-weight: 700;
    text-transform: uppercase;
  }
  
  p {
    margin: 0;
    color: var(--color-primary);
    font-weight: 600;
  }
`;

const GameControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin: 2rem 0;
  padding: 2rem;
  background: var(--color-light-bg);
  border-radius: 18px;
  box-shadow: 0 4px 20px rgba(17, 63, 72, 0.1);
`;

const Input = styled.input`
  padding: 15px 20px;
  font-size: 1rem;
  border: 2px solid var(--color-secondary);
  border-radius: 12px;
  background: var(--color-light-bg);
  color: var(--color-dark);
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
  font-family: 'Nunito', Arial, sans-serif;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    background: var(--color-light-bg);
  }
  
  &::placeholder {
    color: var(--color-dark);
    opacity: 0.6;
  }
`;

const PasswordButton = styled(motion.button)`
  background: var(--color-primary);
  color: var(--color-light-bg);
  border: none;
  padding: 12px 20px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 700;
  font-family: 'Fredoka', Arial, sans-serif;
  text-transform: uppercase;
  
  &:hover {
    background: var(--color-accent);
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #ffffff;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s ease;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.7);
  
  &:hover {
    transform: scale(1.05);
    text-shadow: 4px 4px 8px rgba(0, 0, 0, 0.8);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const RoomCode = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  padding: 18px 28px;
  border-radius: 15px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  color: #2c3e50;
  
  code {
    font-weight: bold;
    font-size: 1.6rem;
    letter-spacing: 3px;
    color: #e74c3c;
    background: #f8f9fa;
    padding: 8px 12px;
    border-radius: 8px;
    border: 2px solid #e74c3c;
  }
  
  @media (max-width: 768px) {
    padding: 14px 20px;
    font-size: 1.1rem;
    
    code {
      font-size: 1.4rem;
      letter-spacing: 2px;
      padding: 6px 10px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 1rem;
    gap: 8px;
    
    code {
      font-size: 1.2rem;
      letter-spacing: 1px;
      padding: 4px 8px;
    }
  }
`;

const GameContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 30px;
  flex: 1;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const WheelSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ControlsSection = styled.div`
  margin-top: 30px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    margin-top: 20px;
    gap: 15px;
  }
  
  @media (max-width: 480px) {
    margin-top: 15px;
    gap: 10px;
    flex-direction: column;
    align-items: center;
  }
`;

const HostControls = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(15px);
  border-radius: 15px;
  padding: 20px;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const HostControlTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 10px;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    font-size: 1.4rem;
  }
`;

const HostButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 20px;
  color: white;
`;

const LoadingSpinner = styled(ClipLoader)`
  margin-top: 20px;
`;

// Festive result popup components
const ResultOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ResultCard = styled(motion.div)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 25px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 3px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  max-width: 500px;
  min-width: 350px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
    background-size: 20px 20px;
    animation: confetti 20s linear infinite;
    pointer-events: none;
  }

  @keyframes confetti {
    0% { transform: rotate(0deg) translateX(0); }
    100% { transform: rotate(360deg) translateX(20px); }
  }
  
  @media (max-width: 768px) {
    max-width: 90vw;
    min-width: 300px;
    padding: 30px;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    min-width: 280px;
    padding: 25px;
    border-radius: 15px;
  }
`;

const ResultHeader = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 20px;
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const ResultPlayer = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  color: #4ecdc4;
  margin-bottom: 15px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;

const ResultPlayerAvatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: white;
  font-weight: bold;
  overflow: hidden;
  border: 4px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const ResultPlayerName = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  color: #4ecdc4;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const ResultAction = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #fff;
  margin: 25px 0;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  animation: glow 2s ease-in-out infinite alternate;

  @keyframes glow {
    from { box-shadow: 0 0 20px rgba(255, 255, 255, 0.2); }
    to { box-shadow: 0 0 30px rgba(255, 255, 255, 0.4); }
  }
`;

const FunnyText = styled.div`
  font-size: 1.3rem;
  color: #ffd93d;
  margin: 20px 0;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  font-style: italic;
`;

const NextTurnButton = styled(motion.button)`
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: #fff;
  border: none;
  padding: 15px 40px;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  margin-top: 20px;
  box-shadow: 0 10px 25px rgba(255, 107, 107, 0.3);
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 15px 35px rgba(255, 107, 107, 0.4);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 5px 15px rgba(255, 107, 107, 0.2);
  }
`;

const LoadingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 20px;
  color: var(--color-light-bg);
  text-align: center;
  
  h2 {
    font-family: 'Fredoka', Arial, sans-serif;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--color-primary);
    margin-bottom: 1rem;
  }
  
  p {
    font-family: 'Nunito', Arial, sans-serif;
    color: var(--color-dark);
    opacity: 0.8;
  }
`;

const ErrorSection = styled.div`
  background: var(--color-light-bg);
  border-radius: 18px;
  padding: 2rem;
  text-align: center;
  color: var(--color-dark);
  border: 2px solid var(--color-primary);
  box-shadow: 0 8px 25px rgba(247, 5, 108, 0.3);
  max-width: 500px;
  margin: 2rem auto;
  
  h2 {
    color: var(--color-primary);
    margin-bottom: 1rem;
    font-family: 'Fredoka', Arial, sans-serif;
    font-weight: 700;
    text-transform: uppercase;
  }
  
  p {
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }
  
  button {
    padding: 12px 24px;
    background: var(--color-primary);
    border: none;
    border-radius: 12px;
    color: var(--color-light-bg);
    cursor: pointer;
    font-weight: 700;
    font-family: 'Fredoka', Arial, sans-serif;
    text-transform: uppercase;
    
    &:hover {
      background: var(--color-accent);
      color: var(--color-dark);
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    }
  }
`;

const HeaderGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const TurnIndicator = styled.div`
  background: var(--color-light-bg);
  backdrop-filter: blur(15px);
  padding: 16px 24px;
  border-radius: 18px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  border-left: 6px solid var(--color-accent);
  border: 2px solid var(--color-secondary);
  box-shadow: 0 6px 20px rgba(77, 186, 191, 0.3);
  color: var(--color-dark);

  strong {
    color: var(--color-primary);
    position: relative;
    display: inline-block;
    font-weight: 800;
    background: var(--color-light-bg);
    padding: 4px 8px;
    border-radius: 6px;
    border: 2px solid var(--color-primary);
    font-family: 'Fredoka', Arial, sans-serif;
    text-transform: uppercase;
  }
  
  @media (max-width: 768px) {
    padding: 14px 20px;
    font-size: 1.1rem;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 1rem;
    margin-bottom: 10px;
    text-align: center;
    
    strong {
      padding: 3px 6px;
      font-size: 0.9rem;
    }
  }
`;

// Error boundary component to catch rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught in boundary:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          margin: '20px', 
          border: '2px solid #ff4d4f',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #fff1f0, #ffebee)',
          textAlign: 'center',
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: '50px'
        }}>
          <h2 style={{ color: '#ff4d4f', marginBottom: '16px' }}>Something went wrong</h2>
          <p style={{ marginBottom: '16px', color: '#666' }}>
            We encountered an error while loading the game. This might be due to:
          </p>
          <ul style={{ textAlign: 'left', marginBottom: '20px', color: '#666' }}>
            <li>Network connectivity issues</li>
            <li>Room data corruption</li>
            <li>Browser compatibility issues</li>
            <li>Temporary server problems</li>
          </ul>
          <div style={{ marginBottom: '20px' }}>
            <strong>Error details:</strong>
            <div style={{ 
              background: '#f5f5f5', 
              padding: '10px', 
              borderRadius: '4px', 
              marginTop: '8px',
              fontSize: '12px',
              fontFamily: 'monospace',
              color: '#d32f2f'
            }}>
              {this.state.error?.message || 'Unknown error'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 24px',
                background: '#ff4d4f',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Refresh Page
            </button>
            <button 
              onClick={() => window.location.href = '/'}
              style={{
                padding: '12px 24px',
                background: '#4ecdc4',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const GameRoom = () => {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isPremium } = useAuth();
  const prevPlayersRef = useRef([]);
  const prevTurnIndexRef = useRef(null);
  const [joinModalShown, setJoinModalShown] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastConnectionCheck, setLastConnectionCheck] = useState(Date.now());
  
  const {
    roomData,
    loading,
    error,
    setError,
    playerId,
    joinRoom,
    updateWheelSegments,
    updateGameSettings,
    spinWheel,
    nextTurn,
    isMyTurn,
    isRoomHost,
    exitGame,
    fetchRoomData,
    canSpinWheel
  } = useGameRoom(roomCode);

  // Initialize analytics tracking with safety checks
  const {
    analytics,
    trackSpin,
    trackPlayerCount,
    saveAnalytics
  } = useRoomAnalytics(roomData?.id, roomCode);

  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showWheelConfig, setShowWheelConfig] = useState(false);
  const [showGameSettings, setShowGameSettings] = useState(false);
  const [turnForceUpdate, setTurnForceUpdate] = useState(0);
  const [joiningPlayers, setJoiningPlayers] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [verifiedPassword, setVerifiedPassword] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  
  // Add connection monitoring
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const startTime = Date.now();
        
        // Create a timeout promise
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Connection check timeout')), 5000)
        );
        
        // Use Supabase to check connection instead of a health endpoint
        const connectionPromise = supabase
          .from('game_rooms')
          .select('id')
          .limit(1);
        
        const { data, error } = await Promise.race([connectionPromise, timeoutPromise]);
        
        const endTime = Date.now();
        
        if (!error && data !== null) {
          setConnectionStatus('connected');
          setLastConnectionCheck(endTime);
        } else {
          setConnectionStatus('slow');
        }
      } catch (error) {
        console.log('Connection check failed:', error);
        setConnectionStatus('disconnected');
      }
    };

    // Check connection every 30 seconds
    const connectionInterval = setInterval(checkConnection, 30000);
    
    // Also check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkConnection();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Initial check
    checkConnection();
    
    return () => {
      clearInterval(connectionInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Add error recovery mechanism
  useEffect(() => {
    const handleError = (event) => {
      console.error('Unhandled error:', event.error);
      setLocalError('An unexpected error occurred. Please refresh the page.');
    };

    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      setLocalError('A network error occurred. Please check your connection and try again.');
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  // Debug to check if player ID and turn status are working correctly
  useEffect(() => {
    try {
      console.log('Current player ID:', playerId);
      console.log('Is my turn:', typeof isMyTurn === 'function' ? isMyTurn() : 'isMyTurn is not a function');
      console.log('Is room host:', typeof isRoomHost === 'function' ? isRoomHost() : 'isRoomHost is not a function');
      console.log('Can spin wheel:', typeof canSpinWheel === 'function' ? canSpinWheel() : 'canSpinWheel is not a function');
    } catch (err) {
      console.error('Error in debug useEffect:', err);
    }
  }, [playerId, isMyTurn, isRoomHost, canSpinWheel, turnForceUpdate]);
  
  // Only show join modal after we've loaded room data and confirmed user isn't already joined
  useEffect(() => {
    try {
      console.log('🔍 Join modal check:', {
        isJoining,
        loading,
        roomData: !!roomData,
        joinModalShown,
        playerId,
        roomCode
      });
      
      if (isJoining) return; // Skip if already joining
      
      // Wait until we have room data and not loading
      if (!loading && roomData && !joinModalShown) {
        // Check if player already joined this room
        const savedPlayerId = localStorage.getItem(`player_${roomCode}`);
        const tempHostId = localStorage.getItem(`temp_host_${roomCode}`);
        
        console.log('🔍 Checking saved player ID:', savedPlayerId);
        console.log('🔍 Checking temp host ID:', tempHostId);
        console.log('🔍 Room host player ID:', roomData.host_player_id);
        
        // Show join modal if:
        // 1. No saved player ID AND no current player ID, OR
        // 2. User is the creator (has temp host ID matching room's host_player_id)
        const shouldShowModal = (!savedPlayerId && !playerId) || 
                               (tempHostId && tempHostId === roomData.host_player_id);
        
        if (shouldShowModal) {
          console.log('✅ User needs to join');
          
          // For private rooms, show password modal first
          if (roomData.is_private && !verifiedPassword) {
            console.log('✅ Showing password modal - private room');
            setShowPasswordModal(true);
          } else {
            console.log('✅ Showing join modal - public room or password verified');
            setShowJoinModal(true);
          }
          
          setJoinModalShown(true);
        } else {
          console.log('❌ User already joined with ID:', savedPlayerId || playerId);
        }
      } else {
        console.log('❌ Conditions not met for join modal:', {
          loading,
          roomData: !!roomData,
          joinModalShown
        });
      }
    } catch (err) {
      console.error('Error in join modal useEffect:', err);
      setLocalError('Error setting up join modal. Please refresh the page.');
    }
  }, [loading, roomData, playerId, roomCode, joinModalShown, isJoining, verifiedPassword]);
  
  // Effect to detect changes in players or turn index
  useEffect(() => {
    try {
      if (roomData && roomData.players && Array.isArray(roomData.players)) {
        // Check if new players have joined
        if (prevPlayersRef.current.length !== roomData.players.length) {
          console.log('Players list changed:', roomData.players);
          
          // Track player count for analytics
          if (typeof trackPlayerCount === 'function') {
            trackPlayerCount(roomData.players.length);
          }
        }
        
        // Check if turn has changed
        if (prevTurnIndexRef.current !== null && prevTurnIndexRef.current !== roomData.current_player_index) {
          console.log('Turn changed to:', roomData.current_player_index);
          
          // Force a re-render by updating a dummy state when turn changes
          // This ensures that all components using isMyTurn() get updated
          const currentPlayer = roomData.players[roomData.current_player_index];
          if (currentPlayer) {
            console.log('Turn changed to player:', currentPlayer.player_name || currentPlayer.name);
            console.log('Is my turn now?', currentPlayer.id === playerId);
          }
          
          // Force re-render to ensure button state updates
          setTurnForceUpdate(prev => prev + 1);
        }
        
        // Update refs with current values
        prevPlayersRef.current = [...roomData.players];
        prevTurnIndexRef.current = roomData.current_player_index;
      }
    } catch (err) {
      console.error('Error in players/turn useEffect:', err);
    }
  }, [roomData, playerId, trackPlayerCount]);

  // Add safety checks for roomCode - moved after all hooks
  if (!roomCode) {
    console.error('No room code provided');
    navigate('/');
    return null;
  }

  // Helper function to get player name with safety checks
  const getPlayerName = (player) => {
    if (!player) return 'Unknown Player';
    return player.player_name || player.name || 'Unknown Player';
  };

  // Password verification handler with error handling
  const handlePasswordVerification = async (password) => {
    try {
      setPasswordError('');
      
      if (!password || !password.trim()) {
        setPasswordError('Password is required');
        return;
      }

      // Add timeout for password verification
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Password verification timeout')), 10000)
      );

      // Verify password with Supabase directly instead of API endpoint
      const passwordCheckPromise = supabase
        .from('game_rooms')
        .select('id, room_code')
        .eq('room_code', roomCode)
        .eq('room_password', password.trim())
        .single();

      const { data: passwordCheck, error: passwordError } = await Promise.race([
        passwordCheckPromise,
        timeoutPromise
      ]);

      if (passwordError || !passwordCheck) {
        setPasswordError('Incorrect password for this room');
      } else {
        setVerifiedPassword(password);
        setShowPasswordModal(false);
        setShowJoinModal(true);
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      if (error.message?.includes('timeout')) {
        setPasswordError('Password verification timed out. Please try again.');
      } else {
        setPasswordError('Failed to verify password. Please try again.');
      }
    }
  };

  // Join room handler with comprehensive error handling
  const handleJoinRoom = async (playerName, avatar, password = null) => {
    try {
      setIsJoining(true);
      setLocalError('');
      
      if (!playerName || !playerName.trim()) {
        throw new Error('Player name is required');
      }

      if (!roomData) {
        throw new Error('Room data not available');
      }

      console.log('Attempting to join room with:', { playerName, avatar, roomCode });
      
      const success = await joinRoom(playerName.trim(), avatar, password);
      
      if (success) {
        console.log('Successfully joined room');
        setShowJoinModal(false);
        setShowPasswordModal(false);
      } else {
        // Check if there's a specific error message from the joinRoom function
        if (error) {
          throw new Error(error);
        } else {
          throw new Error('Failed to join room. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error joining room:', error);
      
      // Provide more specific error messages based on the error type
      let errorMessage = error.message;
      
      if (error.message?.includes('timeout')) {
        errorMessage = 'Connection timeout. Please check your internet connection and try again.';
      } else if (error.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message?.includes('fetch')) {
        errorMessage = 'Failed to connect to the server. Please try again.';
      } else if (error.message?.includes('Incorrect password')) {
        errorMessage = 'Incorrect password for this room. Please try again.';
      } else if (error.message?.includes('already exists')) {
        errorMessage = 'A player with this name already exists in the room. Please choose a different name.';
      } else if (error.message?.includes('Room not found')) {
        errorMessage = 'Room not found. Please check the room code and try again.';
      } else if (error.message?.includes('Room data not available')) {
        errorMessage = 'Unable to load room data. Please refresh the page and try again.';
      }
      
      setLocalError(errorMessage);
    } finally {
      setIsJoining(false);
    }
  };

  // Spin wheel handler with error handling
  const handleSpinWheel = () => {
    try {
      if (!roomData || !roomData.wheel_segments || !Array.isArray(roomData.wheel_segments)) {
        throw new Error('Wheel data not available');
      }

      if (typeof spinWheel !== 'function') {
        throw new Error('Spin function not available');
      }

      const segments = roomData.wheel_segments;
      if (segments.length === 0) {
        throw new Error('No wheel segments available');
      }

      const randomIndex = Math.floor(Math.random() * segments.length);
      const result = segments[randomIndex];
      
      if (!result || !result.text) {
        throw new Error('Invalid wheel segment');
      }

      console.log('Spinning wheel with result:', result.text);
      
      // Track the spin for analytics
      if (typeof trackSpin === 'function') {
        trackSpin(result.text);
      }
      
      spinWheel(result.text);
    } catch (error) {
      console.error('Error spinning wheel:', error);
      setLocalError(error.message || 'Failed to spin wheel. Please try again.');
    }
  };

  // Update wheel segments with error handling
  const handleUpdateWheelSegments = (segments) => {
    try {
      if (!Array.isArray(segments)) {
        throw new Error('Invalid segments data');
      }

      if (typeof updateWheelSegments !== 'function') {
        throw new Error('Update function not available');
      }

      updateWheelSegments(segments);
      setShowWheelConfig(false);
    } catch (error) {
      console.error('Error updating wheel segments:', error);
      setLocalError(error.message || 'Failed to update wheel segments.');
    }
  };

  // Update game settings with error handling
  const handleUpdateGameSettings = (settings) => {
    try {
      if (!settings || typeof settings !== 'object') {
        throw new Error('Invalid settings data');
      }

      if (typeof updateGameSettings !== 'function') {
        throw new Error('Update function not available');
      }

      updateGameSettings(settings);
      setShowGameSettings(false);
    } catch (error) {
      console.error('Error updating game settings:', error);
      setLocalError(error.message || 'Failed to update game settings.');
    }
  };

  // Leave room handler with error handling
  const handleLeaveRoom = async () => {
    try {
      if (typeof exitGame === 'function') {
        await exitGame();
      }
      navigate('/');
    } catch (error) {
      console.error('Error leaving room:', error);
      // Still navigate away even if there's an error
      navigate('/');
    }
  };

  // Get current player name with safety checks
  const getCurrentPlayerName = () => {
    try {
      if (!roomData || !roomData.players || !Array.isArray(roomData.players)) {
        return 'Unknown';
      }

      const currentIndex = roomData.current_player_index;
      if (currentIndex === null || currentIndex === undefined || currentIndex < 0) {
        return 'Unknown';
      }

      const currentPlayer = roomData.players[currentIndex];
      if (!currentPlayer) {
        return 'Unknown';
      }

      return getPlayerName(currentPlayer);
    } catch (error) {
      console.error('Error getting current player name:', error);
      return 'Unknown';
    }
  };

  // Get spin button text with safety checks
  const getSpinButtonText = () => {
    try {
      if (!roomData || !roomData.players || !Array.isArray(roomData.players)) {
        return t('spinTheWheel');
      }

      const playerCount = roomData.players.length;
      const gameSettings = roomData.game_settings || {};
      
      // Solo play (only one player)
      if (playerCount === 1) {
        return t('soloPlay');
      }
      
      // Host-only mode
      if (!gameSettings.allowPlayersToSpin && typeof isRoomHost === 'function' && isRoomHost()) {
        return t('hostSpin');
      }
      
      // Regular turn-based play
      if (typeof isMyTurn === 'function' && isMyTurn()) {
        return t('yourTurn');
      }
      
      // Waiting for other player
      const currentPlayerName = getCurrentPlayerName();
      return `${t('waitingForCurrent')} ${currentPlayerName}`;
    } catch (error) {
      console.error('Error getting spin button text:', error);
      return t('spinTheWheel');
    }
  };

  // Retry connection handler
  const handleRetryConnection = async () => {
    try {
      setIsRetrying(true);
      setLocalError('');
      
      if (typeof fetchRoomData === 'function') {
        await fetchRoomData();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error retrying connection:', error);
      setLocalError('Failed to reconnect. Please refresh the page.');
    } finally {
      setIsRetrying(false);
    }
  };
  
  // Determine if the error is a network connectivity issue
  const isNetworkError = error && (
    error.includes('Network error') || 
    error.includes('Failed to fetch') ||
    error.includes('connect')
  );

  // Combine loading states
  const isPageLoading = loading || isRetrying;

  if (isPageLoading) {
    return (
      <Container>
        <LoadingSection>
          <h2>Loading Game Room...</h2>
          <p>Please wait while we set up the game</p>
          <ClipLoader color="#4ecdc4" size={60} />
          
          {/* Connection status indicator */}
          <div style={{
            marginTop: '20px',
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: connectionStatus === 'connected' ? 'rgba(76, 175, 80, 0.1)' : 
                           connectionStatus === 'slow' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(244, 67, 54, 0.1)',
            border: `1px solid ${connectionStatus === 'connected' ? 'rgba(76, 175, 80, 0.3)' : 
                               connectionStatus === 'slow' ? 'rgba(255, 193, 7, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
            color: connectionStatus === 'connected' ? '#4CAF50' : 
                   connectionStatus === 'slow' ? '#FFC107' : '#F44336',
            fontSize: '0.9rem',
            fontWeight: '500'
          }}>
            {connectionStatus === 'connected' && '🟢 Connected to server'}
            {connectionStatus === 'slow' && '🟡 Slow connection detected'}
            {connectionStatus === 'disconnected' && '🔴 Connection issues detected'}
          </div>
          
          <div style={{
            marginTop: '40px',
            fontSize: '0.8rem',
            color: 'rgba(255, 255, 255, 0.5)',
            fontWeight: '300',
            letterSpacing: '0.5px'
          }}>
            © 2024 Parteezy - Party Responsibly
          </div>
        </LoadingSection>
      </Container>
    );
  }

  if (error || localError) {
    return (
      <Container>
        <ErrorSection>
          <h2>Error Loading Game</h2>
          <p>{error || localError}</p>
          
          {/* Connection status indicator */}
          <div style={{
            marginTop: '15px',
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: connectionStatus === 'connected' ? 'rgba(76, 175, 80, 0.1)' : 
                           connectionStatus === 'slow' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(244, 67, 54, 0.1)',
            border: `1px solid ${connectionStatus === 'connected' ? 'rgba(76, 175, 80, 0.3)' : 
                               connectionStatus === 'slow' ? 'rgba(255, 193, 7, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
            color: connectionStatus === 'connected' ? '#4CAF50' : 
                   connectionStatus === 'slow' ? '#FFC107' : '#F44336',
            fontSize: '0.9rem',
            fontWeight: '500'
          }}>
            {connectionStatus === 'connected' && '🟢 Server connection is working'}
            {connectionStatus === 'slow' && '🟡 Slow connection may be causing issues'}
            {connectionStatus === 'disconnected' && '🔴 Connection issues detected - check your internet'}
          </div>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
            <button onClick={handleRetryConnection} disabled={isRetrying}>
              {isRetrying ? 'Retrying...' : 'Retry'}
            </button>
            <button onClick={() => navigate('/')}>{t('backToHome')}</button>
          </div>
        </ErrorSection>
      </Container>
    );
  }

  if (!roomData) {
    return (
      <Container>
        <ErrorSection>
          <h2>{t('roomNotFound')}</h2>
          <p>{t('roomNotFoundText')}</p>
          <button onClick={() => navigate('/')}>{t('backToHome')}</button>
        </ErrorSection>
      </Container>
    );
  }

  // Main render with comprehensive safety checks
  try {
    return (
      <Container>
        <ServerPriorityIndicator />
        {showPasswordModal && (
          <PasswordModal
            isOpen={true}
            onClose={() => {
              setShowPasswordModal(false);
              navigate('/');
            }}
            onSuccess={handlePasswordVerification}
            error={passwordError}
            isLoading={false}
          />
        )}
        {showJoinModal && (
          <JoinModal 
            isOpen={true}
            onClose={() => {
              // If private room and no player joined yet, redirect to home
              if (roomData?.is_private && !playerId) {
                navigate('/');
              } else {
                setShowJoinModal(false);
              }
            }}
            onJoin={handleJoinRoom}
            error={localError}
            isLoading={isJoining}
            roomData={roomData}
          />
        )}
        {showWheelConfig && roomData && (
          <WheelConfig
            segments={roomData.wheel_segments || []}
            onSave={handleUpdateWheelSegments}
            onClose={() => setShowWheelConfig(false)}
          />
        )}
        {showGameSettings && roomData && (
          <GameSettings
            settings={roomData.game_settings || {}}
            onSave={handleUpdateGameSettings}
            onClose={() => setShowGameSettings(false)}
          />
        )}
        
        <Header>
          <Title onClick={() => navigate('/')}>Parteezy</Title>
          <HeaderGroup>
            <RoomCode>
              {t('roomCode')}: <code>{roomCode}</code>
            </RoomCode>
            <LanguageDropdown />
            <Button 
              isSecondary={true}
              onClick={handleLeaveRoom}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('leaveRoom')}
            </Button>
          </HeaderGroup>
        </Header>
        
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '15px',
          fontSize: '0.7rem',
          color: 'rgba(255, 255, 255, 0.4)',
          fontWeight: '300',
          letterSpacing: '0.5px',
          userSelect: 'none',
          zIndex: 1000
        }}>
          © 2024 Parteezy
        </div>
        
        {/* Result overlay for spin results */}
        {roomData && roomData.current_spin_result && (
          <ResultOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ResultCard
              initial={{ scale: 0.5, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.5, y: 50 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              <ResultHeader>
                {t('spinResult')}
              </ResultHeader>
              
              <ResultPlayer>
                {(() => {
                  try {
                    // Find the current player who spun the wheel
                    const currentPlayer = roomData.players?.find(p => 
                      p.player_name === roomData.current_spin_player
                    );
                    
                    return (
                      <>
                        {/* Only show avatar if player has uploaded a profile image */}
                        {currentPlayer?.avatar_url && (
                          <ResultPlayerAvatar>
                            <img 
                              src={currentPlayer.avatar_url} 
                              alt={`${roomData.current_spin_player}'s avatar`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </ResultPlayerAvatar>
                        )}
                        <ResultPlayerName>
                          {roomData.current_spin_player || 'Unknown Player'}
                        </ResultPlayerName>
                      </>
                    );
                  } catch (err) {
                    console.error('Error rendering result player:', err);
                    return <ResultPlayerName>Unknown Player</ResultPlayerName>;
                  }
                })()}
              </ResultPlayer>
              
              <ResultAction>
                {(() => {
                  try {
                    // Use the same field as the wheel to ensure consistency
                    const displayResult = roomData.current_spin_result || roomData.wheel_target_result;
                    console.log('🎪 Popup showing result:', {
                      current_spin_result: roomData.current_spin_result,
                      wheel_target_result: roomData.wheel_target_result,
                      displayResult: displayResult,
                      current_spin_player: roomData.current_spin_player
                    });
                    return displayResult || 'Unknown Result';
                  } catch (err) {
                    console.error('Error getting display result:', err);
                    return 'Unknown Result';
                  }
                })()}
              </ResultAction>
              
              {(() => {
                try {
                  return (isMyTurn() || isRoomHost()) && (
                    <NextTurnButton
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={nextTurn}
                    >
                      {(() => {
                        try {
                          // Solo host playing alone
                          if (roomData.players?.length === 1) {
                            return t('awesome');
                          }
                          
                          // Multiple players
                          return t('nextTurn');
                        } catch (err) {
                          console.error('Error getting next turn text:', err);
                          return 'Continue';
                        }
                      })()}
                    </NextTurnButton>
                  );
                } catch (err) {
                  console.error('Error rendering next turn button:', err);
                  return null;
                }
              })()}
            </ResultCard>
          </ResultOverlay>
        )}
        
        {/* Main game content */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          width: '100%',
          maxWidth: '1200px'
        }}>
          {/* Room info */}
          <RoomInfo>
            <h2>Room: {roomCode}</h2>
            {roomData.is_private && <p>🔒 Private Room</p>}
            <p>Players: {roomData.players?.length || 0}</p>
            {roomData.players?.length > 1 && (
              <p>Current Turn: {getCurrentPlayerName()}</p>
            )}
          </RoomInfo>

          {/* Game content with wheel and player list side by side */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            width: '100%',
            maxWidth: '1200px'
          }}>
            {/* Spinning wheel */}
            {roomData.wheel_segments && Array.isArray(roomData.wheel_segments) && roomData.wheel_segments.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <SpinningWheel
                  segments={roomData.wheel_segments}
                  onSpin={handleSpinWheel}
                  isSpinning={roomData.is_wheel_spinning || false}
                  canSpin={typeof canSpinWheel === 'function' ? canSpinWheel() : false}
                  spinButtonText={getSpinButtonText()}
                  targetResult={roomData.wheel_target_result}
                  gameSettings={roomData.game_settings || {}}
                />
                
                {/* Spin button */}
                <Button
                  onClick={handleSpinWheel}
                  disabled={!canSpinWheel() || roomData.is_wheel_spinning}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    marginTop: '20px',
                    minWidth: '200px',
                    fontSize: '1.2rem',
                    padding: '15px 30px'
                  }}
                >
                  {getSpinButtonText()}
                </Button>
              </div>
            )}

            {/* Player list */}
            {roomData.players && Array.isArray(roomData.players) && (
              <PlayerList
                players={roomData.players}
                currentPlayerId={playerId}
                currentTurnIndex={roomData.current_player_index}
                hostPlayerId={roomData.host_player_id}
              />
            )}
          </div>

          {/* Game controls */}
          {typeof isRoomHost === 'function' && isRoomHost() && (
            <GameControls>
              <Button
                onClick={() => setShowWheelConfig(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('customizeWheel')}
              </Button>
              <Button
                onClick={() => setShowGameSettings(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('gameSettings')}
              </Button>
            </GameControls>
          )}

          {/* QR Code for joining */}
          <QRCodeGenerator roomCode={roomCode} />
        </div>
      </Container>
    );
  } catch (renderError) {
    console.error('Error rendering GameRoom:', renderError);
    return (
      <Container>
        <ErrorSection>
          <h2>Rendering Error</h2>
          <p>Failed to render the game room. Please refresh the page.</p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
          <button onClick={() => navigate('/')}>Go Home</button>
        </ErrorSection>
      </Container>
    );
  }
};

const GameRoomWithErrorBoundary = () => (
  <ErrorBoundary>
    <GameRoom />
  </ErrorBoundary>
);

export default GameRoomWithErrorBoundary; 